import { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { useAccount } from '../../contexts/AccountContext';
import { useFeatures } from '../../hooks/useFeatures';
import { submitOrderSource } from '../../utils/api';
import { readSession, writeSession } from '../../core/storage';
import { normalizeSource } from '../../core/rules/acquisition';
import { ease } from '../../core/format/motion';
import { acquisitionQuestion } from '../../data/acquisition';
import { founder } from '../../data/content';
import { btnSecondary } from '../../ui/styles';
import { SourceChips } from '../shared/SourceChips';
import type { AcquisitionSource } from '../../core/types/insights';
type Props = {
  orderId: string;
  /** Uit `?t=` van de terugkeerlink; leeg = de sessie van de order (direct na afrekenen). */
  token: string;
  /** Zegt de server dat het mag? Onbekend (oude server, direct na afrekenen) = wel vragen. */
  ask: boolean | undefined;
};
const answeredKey = (orderId: string) => `bb-source-${orderId}`;

/**
 * Onderaan de bedankpagina, ná betalen (dus nooit een drempel voor de bestelling): één tik op
 * "Hoe kwam je bij ons?". Alleen als de server het nog niet weet. Ververs je de pagina, dan blijft
 * het bedankje staan in plaats van de vraag opnieuw.
 */
export function OrderSourceAsk({
  orderId,
  token,
  ask
}: Props) {
  const reduce = useReducedMotion();
  const {
    acquisitionQuestion: enabled
  } = useFeatures();
  const {
    account
  } = useAccount();
  const [value, setValue] = useState<AcquisitionSource | null>(null);
  const [state, setState] = useState<'open' | 'sending' | 'done' | 'error'>(() => readSession(answeredKey(orderId)) ? 'done' : 'open');
  // Zo was het bij het openen: na het antwoord blijft het bedankje staan.
  const [shown] = useState(() => enabled && ask !== false && !account?.customer.acquisitionSource);
  if (!shown) return null;
  const send = async (source: AcquisitionSource) => {
    const clean = normalizeSource(source);
    if (!clean) return;
    setState('sending');
    try {
      await submitOrderSource(orderId, token, {
        source: clean
      });
      writeSession(answeredKey(orderId), '1');
      setState('done');
    } catch {
      setState('error');
    }
  };
  return <section aria-labelledby="source-question" className="mt-12 border-t border-line pt-8" aria-live="polite" data-id="element-7068" data-mp="2dppq3xlclut3">
      <AnimatePresence mode="wait" initial={false} data-id="element-7069" data-mp="1g278dx9plicy">
        {state === 'done' ? <motion.p key="done" initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 4
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease
      }} id="source-question" className="flex items-center gap-2 text-[15px] text-ink" data-id="element-7070" data-mp="1l11zrnlqsf8o">
            <CheckIcon className="h-4 w-4 text-clay" strokeWidth={1.75} aria-hidden="true" data-id="element-7071" data-mp="31fti17p6c649" />
            Dank je wel, dat helpt {founder.firstName} echt.
          </motion.p> : <motion.div key="ask" exit={{
        opacity: 0
      }} transition={{
        duration: 0.15,
        ease
      }} data-id="element-7072" data-mp="uqrtrczl8x38">
            <SourceChips id={`source-${orderId}`} value={value} onChange={setValue} onPick={s => void send(s)} disabled={state === 'sending'} legend={<span id="source-question" data-id="element-7074" data-mp="g2n5zulohvdl">Nog één vraag: {acquisitionQuestion.charAt(0).toLowerCase() + acquisitionQuestion.slice(1)}</span>} legendClassName="font-display text-xl font-light text-ink" hint="Helemaal vrijwillig. Zo weet ik waar ik mijn tijd in moet steken. Ik koppel het niet aan advertenties." data-id="element-7073" data-mp="prydjcsx2rzd" />
            {value?.channel === 'anders' && <button type="button" onClick={() => void send(value)} disabled={state === 'sending'} className={`${btnSecondary} mt-3`} data-id="element-7075" data-mp="3t96xc277g1wq">
                {state === 'sending' ? 'Versturen…' : 'Versturen'}
              </button>}
            {state === 'error' && <p role="alert" className="mt-3 text-[14px] text-clay" data-id="element-7076" data-mp="p3w59u9yu1k8">
                Dat lukte even niet. Tik nog eens, of laat het gerust zitten.
              </p>}
          </motion.div>}
      </AnimatePresence>
    </section>;
}